import { formatCents } from '@src/common';
import { orderSaleItems } from '@src/common/order';
import { CartCouponTypeEnum, PaymentOption } from '@src/core/buyerApiClient';
import { useAppSelector } from '@src/core/hooks';
import { currentOrderSelector } from '@src/features/order';
import { currentSaleSelector } from '@src/features/sale';
import useTranslate from '@src/i18n/useTranslate';
import React, { useMemo, useState } from 'react';
import ArrowDown from '../../icons/arrow-down-icon.svg';

import { groupSaleItemsByProductPackage } from '../../common/checkout';
import PackageItemsGroup from './Package/ProductPackageGroup';
import { SaleItemWithPrice } from './SaleItem';
import * as styles from './styles';

const {
  CouponContainer,
  PurchaseResumeContent,
  TotalWithoutDiscountHeader,
  TotalWithoutDiscountValue,
  TotalWithoutDiscountContainer,
  TotalText,
  TotalPrice,
  Total,
  TotalContainer,
  Container,
  Header,
  ResumeTitle,
  ProductsResume,
  PurchaseResumeTotal,
  Coupon,
  CouponText,
  CouponCode,
  CouponValue,
} = styles;

export const PurchaseResume = ({ isResponsive = false, paymentOption }: PurchaseResumeProps) => {
  const { i18nElement, i18n } = useTranslate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const order = useAppSelector(currentOrderSelector)!;
  const sale = useAppSelector(currentSaleSelector)!;

  const items = useMemo(() => {
    if (order) {
      return orderSaleItems(order);
    }

    if (sale.items) {
      return sale.items;
    }

    return [];
  }, [order, sale]);

  const groupedSaleItems = useMemo(() => groupSaleItemsByProductPackage(items), [items]);

  const totalAmountCents = useMemo(() => {
    if (order) {
      return order.finalAmountCents;
    }

    if (sale) {
      return sale.totalAmountCents;
    }

    return 0;
  }, [order, sale]);

  const coupon = useMemo(() => {
    if (order) {
      return order.sales.find((sale) => sale.coupon)?.coupon;
    }

    if (sale) {
      return sale.coupon;
    }

    return undefined;
  }, [order, sale]);

  return (
    <Container aria-label='resume container'>
      <Header isResponsive={isResponsive} onClick={() => (isResponsive ? setIsCollapsed((state) => !state) : null)}>
        <ResumeTitle>{i18nElement('checkout_purchase_summary')}</ResumeTitle>

        {isResponsive ? <ArrowDown /> : null}
      </Header>

      <PurchaseResumeContent isCollapsed={isCollapsed} isResponsive={isResponsive}>
        <ProductsResume aria-label='products resume' id='products_resume'>
          {groupedSaleItems.packages.map((packageItem, index) => (
            <PackageItemsGroup
              key={`${packageItem.productPackage.name}-${index}`}
              productPackage={packageItem.productPackage}
              items={packageItem.items}
            />
          ))}

          {groupedSaleItems.nonPackageItems?.map((item) => (
            <SaleItemWithPrice key={item.id} item={item} />
          ))}
        </ProductsResume>

        <PurchaseResumeTotal>
          {coupon ? (
            <CouponContainer>
              <Coupon>
                <CouponText>{i18n('cart_coupon_text')}</CouponText>

                <CouponCode>{coupon.code}</CouponCode>
              </Coupon>

              <Coupon>
                <CouponText>{i18n('cart_discount')}</CouponText>

                <CouponValue>
                  {coupon?.type === CartCouponTypeEnum.Percentage ? (
                    <span>{`(-${coupon.formattedDiscount})`}</span>
                  ) : null}
                  {formatCents(coupon.fullAmountCents - coupon.finalAmountCents)}
                </CouponValue>
              </Coupon>
            </CouponContainer>
          ) : null}

          <TotalContainer>
            {coupon ? (
              <TotalWithoutDiscountContainer>
                <TotalWithoutDiscountHeader>{i18n('cart_totalLabel')}</TotalWithoutDiscountHeader>
                <TotalWithoutDiscountValue>{formatCents(coupon.fullAmountCents)}</TotalWithoutDiscountValue>
              </TotalWithoutDiscountContainer>
            ) : null}

            <Total>
              <TotalText>{i18n('cart_final_value')}</TotalText>

              <TotalPrice>{formatCents(paymentOption?.amountCents ?? totalAmountCents)}</TotalPrice>
            </Total>
          </TotalContainer>
        </PurchaseResumeTotal>
      </PurchaseResumeContent>
    </Container>
  );
};

interface PurchaseResumeProps {
  isResponsive?: boolean;
  paymentOption: PaymentOption | undefined;
}
